import { mobilePlugins } from "./mobileHelper";
import * as datadog from "@/shared/datadog";

/**
 * Scan result returned to app code.
 */
export interface CapacitorBarcodeScannerScanResult {
  ScanResult: string;
  format: CapacitorBarcodeScannerTypeHint;
}

export enum Html5QrcodeSupportedFormats {
    QR_CODE	= 0,
    AZTEC = 1,
    CODABAR = 2,
    CODE_39 = 3,
    CODE_93	= 4,
    CODE_128 = 5,
    DATA_MATRIX = 6,
    MAXICODE = 7,
    ITF = 8,
    EAN_13 = 9,
    EAN_8 = 10,
    PDF_417 = 11,
    RSS_14 = 12,
    RSS_EXPANDED = 13,
    UPC_A = 14,
    UPC_E = 15,
    UPC_EAN_EXTENSION = 16
}

/**
 * Enum representing a special option to indicate that all barcode types are supported.
 */
export enum CapacitorBarcodeScannerTypeHintALLOption {
  ALL = 17
}

export type CapacitorBarcodeScannerTypeHint =
  | Html5QrcodeSupportedFormats
  | CapacitorBarcodeScannerTypeHintALLOption;

/**
 * Enum representing the orientation of the scanner during barcode scanning.
 */
export enum CapacitorBarcodeScannerScanOrientation {
  PORTRAIT = 1,
  LANDSCAPE = 2,
  ADAPTIVE = 3,
}

/**
 * Enum representing the library to be used for barcode scanning on Android devices.
 */
export enum CapacitorBarcodeScannerAndroidScanningLibrary {
  ZXING = "zxing",
  MLKIT = "mlkit",
}

export enum CapacitorBarcodeScannerCameraDirection {
    BACK = 1,
    FRONT = 2,
}

export async function scanWithOptions (options?: {
  hint?: CapacitorBarcodeScannerTypeHint;
  scanInstructions?: "Please scan the barcode";
  scanButton?: false;
  scanText?: " "; // label for the scan button if shown
  cameraDirection?: CapacitorBarcodeScannerCameraDirection;
  scanOrientation?: CapacitorBarcodeScannerScanOrientation;
  android?: {
    scanningLibrary?: CapacitorBarcodeScannerAndroidScanningLibrary;
  };
  web?: {
    showCameraSelection?: boolean;
    scannerFPS?: number;
  };
}): Promise<CapacitorBarcodeScannerScanResult | null> {
  const granted = await ensurePermission(true);
  if (!granted) {
    datadog.log("Barcode scanner failed. Unable to access user's camera.", {}, "info");
    (window as any).navigator.notification.alert("Please check your device settings to make sure camera permission is allowed for this app.", null, "Unable to access your camera");
    return Promise.resolve(null);
  }
  try {
    const result = await mobilePlugins.CapacitorBarcodeScanner.scanBarcode({
      hint: options?.hint ?? CapacitorBarcodeScannerTypeHintALLOption.ALL,
      scanInstructions: options?.scanInstructions,
      scanButton: options?.scanButton,
      scanText: options?.scanText,
      cameraDirection: options?.cameraDirection,
      scanOrientation: options?.scanOrientation,
      android: options?.android,
      web: options?.web,
    });
    if (result && result.ScanResult && result.ScanResult.length > 0) {
      return result;
    } else {
      const data = {
        code: result?.ScanResult,
        format: result?.format
      };
      throw new Error(`Scanner returned invalid result. Data: ${ JSON.stringify(data)}`);
    }
  } catch (err) {
    if (err instanceof Error) {
      if (err.message.includes("cancelled")) {
        return Promise.resolve(null);
      }
    }
    datadog.log("Barcode scanner error:", err, "error");
    throw err;
  }
}

/** Ask or check for camera permission */
export async function ensurePermission (force = false): Promise<boolean> {
  try {
    const checkPermissionResult = await mobilePlugins.Camera.checkPermissions();
    if (checkPermissionResult && checkPermissionResult.camera && checkPermissionResult.camera === "granted") {
        return true;
    }

   const requestPermissionsResult =  await mobilePlugins.Camera.requestPermissions({ permissions: ["camera"]});
    if (requestPermissionsResult && requestPermissionsResult.camera && requestPermissionsResult.camera === "granted") {
        return true;
    }

    return false;
  } catch (err) {
    datadog.log("Barcode scanner camera checkPermission error:", err, "error");
    return false;
  }
}

export function scanBarcode (): Promise<CapacitorBarcodeScannerScanResult | null> {
  return scanWithOptions({
    hint: CapacitorBarcodeScannerTypeHintALLOption.ALL,
    scanInstructions: "Please scan the barcode",
    scanButton: false, scanText: " ",
    cameraDirection: CapacitorBarcodeScannerCameraDirection.BACK,
    scanOrientation: CapacitorBarcodeScannerScanOrientation.PORTRAIT
  });
}